import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, TextField, Button,
  CircularProgress, MenuItem, Select, FormControl, InputLabel,
  Collapse, Tooltip, IconButton,
} from '@mui/material';
import { ConfirmDialog } from './ui/confirm-dialog';
import {
  Terminal as TerminalIcon,
  PlayArrow as RunIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Warning as WarningIcon,
  HourglassEmpty as HourglassIcon,
  InfoOutlined as InfoIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useExecuteShellCommand, useActionAudits } from '@/hooks/use-devices';
import { ACTION_SHELL_EXECUTE, type DeviceActionAudit } from '@/types';
import { colors, SHELL_MONO } from '@/theme/tokens';
import { ShellOutput } from '@/components/shell-output';
import {
  isCommandRisky,
  isLongRunning,
  RISKY_SHELL_PREFIXES,
  SHELL_TIMEOUT_OPTIONS,
} from '@/lib/shell-command';


// How many commands the transcript shows — keep the fetch, the cap, and the
// caption in sync.
const MAX_HISTORY = 10;

// One executed command and its outcome. Two sources feed the transcript:
//   - live: commands run this session (id "local-N"), shown expanded so the
//     operator sees the result immediately.
//   - seeded: the 10 latest shell audits fetched from the server on load
//     (id = audit UUID, `seeded: true`) so the history survives a reload.
type CommandEntry = {
  id: string;
  command: string;
  status: 'running' | 'done' | 'error';
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  error?: string;
  seeded?: boolean;
  time?: string; // ISO timestamp the command was executed
};

// Map a persisted shell audit row into a transcript entry.
function auditToEntry(a: DeviceActionAudit): CommandEntry {
  const rd = a.result_data ?? {};
  return {
    id: a.id,
    command: (a.params?.command as string | undefined) ?? '',
    status: a.error ? 'error' : 'done',
    stdout: rd.stdout as string | undefined,
    stderr: rd.stderr as string | undefined,
    exitCode: rd.exitCode as number | undefined,
    error: a.error,
    seeded: true,
    time: a.executed_at,
  };
}

// Compact clock for the header — time only for today, +date otherwise.
function fmtClock(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtFull(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// A single terminal block: a clickable `$ command` prompt line that toggles
// its output. Open state is parent-controlled; blocks open/close independently.
function CommandResult({ entry, open, onToggle }: { entry: CommandEntry; open: boolean; onToggle: () => void }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation(); // don't toggle the block open/closed
    // Copy the command itself (so it can be re-run), not its output.
    navigator.clipboard?.writeText(entry.command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden', bgcolor: colors.background.paper }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, cursor: 'pointer',
          bgcolor: colors.background.tableHead,
          borderBottom: open ? '1px solid #E5E7EB' : 'none',
        }}
      >
        {open ? <ArrowUpIcon sx={{ fontSize: 16, color: colors.text.secondary, flexShrink: 0 }} /> : <ArrowDownIcon sx={{ fontSize: 16, color: colors.text.secondary, flexShrink: 0 }} />}
        <Tooltip title={copied ? 'Copied' : 'Copy command'}>
          <IconButton size="small" onClick={handleCopy} sx={{ flexShrink: 0, p: 0.25, color: colors.text.secondary }}>
            {copied ? <CheckIcon sx={{ fontSize: 15, color: colors.success.main }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
        <Typography sx={{ flex: 1, fontFamily: SHELL_MONO, fontSize: 12, color: colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ color: colors.primary.main, mr: 0.5 }}>$</Box>
          {entry.command}
        </Typography>
        {entry.status === 'running' && (
          <CircularProgress size={14} sx={{ color: colors.text.secondary, flexShrink: 0 }} />
        )}
        {entry.time && (
          <Tooltip title={fmtFull(entry.time)}>
            <Typography sx={{ flexShrink: 0, fontSize: 11, color: colors.text.tertiary, whiteSpace: 'nowrap' }}>
              {fmtClock(entry.time)}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 1 }}>
          <ShellOutput
            running={entry.status === 'running'}
            stdout={entry.stdout}
            stderr={entry.stderr}
            error={entry.error}
            maxHeight={300}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

interface ShellCommandExecutorProps {
  deviceId: string;
  isOnline: boolean;
}

export function ShellCommandExecutor({ deviceId, isOnline }: ShellCommandExecutorProps) {
  const [command, setCommand] = useState('');
  const [timeout, setTimeout] = useState(30000);
  const [history, setHistory] = useState<CommandEntry[]>([]);
  // Which blocks are expanded. Blocks open/close independently — opening one
  // never collapses another. New commands start expanded; seeded history
  // starts collapsed (except the most recent).
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const seqRef = useRef(0);
  // Synchronous lock: only one command runs at a time. A ref (not isPending)
  // so two rapid Enter presses in the same frame can't both slip through
  // before React re-renders.
  const runningRef = useRef(false);
  const [confirmRisky, setConfirmRisky] = useState<string | null>(null);
  const [confirmLongRun, setConfirmLongRun] = useState<string | null>(null);
  const longRun = useMemo(() => isLongRunning(command), [command]);
  // Recompute hit info for whatever command sits in a pending confirm.
  // We can't rely on `longRun` because the input is cleared as soon as the
  // command is submitted (we keep the captured cmd in state).
  const pendingLongRunHit = useMemo(
    () => (confirmLongRun ? isLongRunning(confirmLongRun) : { match: false as const }),
    [confirmLongRun],
  );
  const pendingRiskyLongRunHit = useMemo(
    () => (confirmRisky ? isLongRunning(confirmRisky) : { match: false as const }),
    [confirmRisky],
  );
  const executeCommand = useExecuteShellCommand(deviceId);
  // The 10 latest shell audits from the server — seeds the transcript on load
  // so previously-run commands survive a reload.
  const audits = useActionAudits(deviceId, { limit: MAX_HISTORY, action: ACTION_SHELL_EXECUTE });

  // Seed once: the first time the server history resolves, append it below any
  // commands already run this session. seededRef stops later refetches (the
  // execute mutation invalidates this query) from duplicating rows.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !audits.data) return;
    seededRef.current = true;
    const seeded = (audits.data.audits ?? []).map(auditToEntry);
    if (seeded.length) {
      setHistory((prev) => [...prev, ...seeded]);
      // Expand the most recent past command if nothing is open yet.
      setOpenIds((cur) => (cur.size ? cur : new Set([seeded[0].id])));
    }
  }, [audits.data]);

  const toggleOpen = (id: string) =>
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runCommand = (cmd: string) => {
    if (runningRef.current) return; // one command at a time
    runningRef.current = true;
    const id = `local-${++seqRef.current}`;
    // Push a running entry to the top of the transcript, then clear the input.
    // Expand it without collapsing anything already open.
    setHistory((prev) => [{ id, command: cmd, status: 'running', time: new Date().toISOString() }, ...prev]);
    setOpenIds((cur) => new Set(cur).add(id));
    setCommand('');
    executeCommand.mutate(
      { command: cmd, timeoutMs: timeout },
      {
        onSuccess: (data) => {
          setHistory((prev) => prev.map((e) => e.id === id ? {
            ...e,
            status: 'done',
            stdout: data.response?.stdout,
            stderr: data.response?.stderr,
            exitCode: data.response?.exitCode,
          } : e));
        },
        onError: (err) => {
          setHistory((prev) => prev.map((e) => e.id === id ? {
            ...e,
            status: 'error',
            error: err instanceof Error ? err.message : 'Command execution failed',
          } : e));
        },
        onSettled: () => { runningRef.current = false; },
      }
    );
  };

  const handleExecute = () => {
    const cmd = command.trim();
    if (!cmd) return;
    if (executeCommand.isPending || runningRef.current) return; // a command is already running
    // Risky takes precedence — its confirm dialog already includes the
    // long-running note when both conditions apply.
    if (isCommandRisky(cmd)) {
      setConfirmRisky(cmd);
      return;
    }
    if (isLongRunning(cmd).match) {
      setConfirmLongRun(cmd);
      return;
    }
    runCommand(cmd);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Command Input */}
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TerminalIcon sx={{ color: '#666666', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>Execute Shell Command</Typography>
            </Box>
          }
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. dumpsys battery, getprop ro.build.version.sdk"
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleExecute(); } }}
              disabled={!isOnline || executeCommand.isPending}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Timeout</InputLabel>
              <Select value={timeout} label="Timeout" onChange={(e) => setTimeout(Number(e.target.value))}>
                {SHELL_TIMEOUT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.ms} value={opt.ms}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip
              title={
                !isOnline
                  ? 'Device is offline'
                  : !command.trim()
                  ? 'Enter a command to run'
                  : executeCommand.isPending
                  ? 'Running…'
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={executeCommand.isPending ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                  disabled={!isOnline || !command.trim() || executeCommand.isPending}
                  onClick={handleExecute}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Execute
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Tooltip
            placement="bottom-start"
            title={
              <Box sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>
                {Array.from(RISKY_SHELL_PREFIXES).join(', ')}
              </Box>
            }
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, color: '#666', cursor: 'help', width: 'fit-content' }}>
              <WarningIcon sx={{ fontSize: 14, color: colors.warning.main }} />
              <Typography variant="caption">
                {RISKY_SHELL_PREFIXES.size} risky commands require confirmation
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip
            placement="bottom-start"
            title={
              <Box sx={{ fontSize: 11, maxWidth: 340, whiteSpace: 'normal' }}>
                Commands run inside the MDM agent's own user sandbox — <strong>not</strong> the adb
                shell user (uid 2000) and <strong>not</strong> root. adb-style or root-requiring
                commands (su, writing system files, setenforce, remount) return a permission error,
                and file access is limited to what the agent can reach. A "permission denied" or
                empty result usually means the agent's user lacks access — not that the command is
                wrong.
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: '#666', cursor: 'help', width: 'fit-content' }}>
              <InfoIcon sx={{ fontSize: 14, color: '#666' }} />
              <Typography variant="caption">
                Runs as the MDM agent (sandboxed) — not adb shell or root
              </Typography>
            </Box>
          </Tooltip>

          {longRun.match && (
            <Box
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, p: 1, borderRadius: 1,
                bgcolor: 'rgba(245, 158, 11, 0.08)', border: `1px solid ${colors.warning.main}33`,
              }}
            >
              <HourglassIcon sx={{ fontSize: 16, color: colors.warning.main, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" component="div">
                  <strong>Long-running command:</strong> {longRun.reason}. It will be killed
                  by the {Math.round(timeout / 1000)}s timeout — {longRun.suggestion}.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: colors.text.tertiary }}>
                  You'll be asked to confirm before it runs.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Transcript: newest command on top. Live commands render expanded;
              seeded history (the 10 latest from the server) renders collapsed.
              Replaces the old single-output box + separate audit-history card. */}
          {history.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: colors.text.tertiary }}>
                Showing latest {MAX_HISTORY} commands
              </Typography>
              {history.slice(0, MAX_HISTORY).map((entry) => (
                <CommandResult
                  key={entry.id}
                  entry={entry}
                  open={openIds.has(entry.id)}
                  onToggle={() => toggleOpen(entry.id)}
                />
              ))}
            </Box>
          ) : audits.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No commands run yet.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Risky-command confirm dialog. The backend runs anything we send;
          this is the operator's acknowledgement for destructive
          first-tokens. Uses the shared ConfirmDialog so styling matches
          the rest of the app (brand-orange confirm, neutral cancel). */}
      <ConfirmDialog
        open={!!confirmRisky}
        onClose={() => setConfirmRisky(null)}
        onConfirm={() => {
          const cmd = confirmRisky;
          setConfirmRisky(null);
          if (cmd) runCommand(cmd);
        }}
        title="Run risky command?"
        icon={<WarningIcon sx={{ color: colors.warning.main }} />}
        description={
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This command can affect device state or data. Review before running.
            </Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: 13, bgcolor: '#1E1E1E', color: '#D4D4D4', p: 1.5, borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {confirmRisky}
            </Box>
            {pendingRiskyLongRunHit.match && (
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: colors.warning.main }}>
                <HourglassIcon sx={{ fontSize: 14 }} />
                Also long-running — {pendingRiskyLongRunHit.reason}; killed at {Math.round(timeout / 1000)}s.
              </Typography>
            )}
          </>
        }
        confirmLabel="Run anyway"
        destructive
      />

      {/* Long-running confirm. Soft variant of the risky dialog — same
          pattern, but copy explains the *timeout* concern, not safety. */}
      <ConfirmDialog
        open={!!confirmLongRun}
        onClose={() => setConfirmLongRun(null)}
        onConfirm={() => {
          const cmd = confirmLongRun;
          setConfirmLongRun(null);
          if (cmd) runCommand(cmd);
        }}
        title="Run long-running command?"
        icon={<HourglassIcon sx={{ color: colors.warning.main }} />}
        description={
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {pendingLongRunHit.match ? (
                <>
                  <strong>{pendingLongRunHit.reason}</strong> — the command will be killed by the {Math.round(timeout / 1000)}s timeout.
                  {' '}{pendingLongRunHit.suggestion[0].toUpperCase() + pendingLongRunHit.suggestion.slice(1)}.
                </>
              ) : (
                'This command does not stop on its own and will be killed at the timeout.'
              )}
            </Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: 13, bgcolor: '#1E1E1E', color: '#D4D4D4', p: 1.5, borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {confirmLongRun}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: colors.text.tertiary }}>
              Note: output is only captured after the command exits, so a killed command usually produces no stdout/stderr.
            </Typography>
          </>
        }
        confirmLabel="Run anyway"
      />
    </Box>
  );
}
